// src/lib/finance.ts

/* ---------- Общие утилиты ---------- */
export type InputMode = "RateOnly" | "AmountUsdtOnly" | "Both";

export const round2 = (v: number | null | undefined) =>
  v != null && isFinite(+v) ? +(+v).toFixed(2) : 0;

export const round4 = (v: number | null | undefined) =>
  v != null && isFinite(+v) ? +(+v).toFixed(4) : 0;

/* ---------- Платформенные параметры ---------- */
export function getMode(p: any): InputMode {
  const m = p?.inputMode ?? p?.InputMode;
  if (typeof m === "string") return m as InputMode;
  return ({ 1: "RateOnly", 2: "AmountUsdtOnly", 3: "Both", 0: "RateOnly" } as any)[m] || "Both";
}

/** Наценка: 8.5 -> 0.085; 0.085 -> 0.085 */
export function getMarkup(p: any): number {
  const raw = +(p?.markupPercent ?? p?.MarkupPercent ?? p?.markup ?? 0);
  if (!isFinite(raw) || raw <= 0) return 0;
  return raw > 1 ? raw / 100 : raw;
}

/* ---------- Пересчёты курса / USDT ---------- */
export function rateFromUsdt(
  amountFiat: number | null | undefined,
  usdt: number | null | undefined,
  markupPercent?: number | null | undefined
): number | null {
  const a = +amountFiat! || 0;
  const u = +usdt! || 0;
  const mp = !isFinite(+markupPercent!) || +markupPercent! <= 0
    ? 0
    : (+markupPercent! > 1 ? +markupPercent! / 100 : +markupPercent!);

  if (a > 0 && u > 0) {
    // Процент трейдера удерживается ИЗ объёма USDT, а не начисляется поверх курса:
    // реально списывается u*(1-mp), поэтому курс = a / (u*(1-mp)) = base / (1-mp).
    if (mp >= 1) return null;
    const base = a / u;
    return round4(base / (1 - mp));
  }
  return null;
}

export function usdtFromRate(
  amountFiat: number | null | undefined,
  rate: number | null | undefined
): number | null {
  const a = +amountFiat! || 0;
  const r = +rate! || 0;
  if (a > 0 && r > 0) return round4(a / r);
  return null;
}

/** Универсальный эффективный курс по текущему режиму ввода */
export function calcEffectiveRate(args: {
  amountFiat?: number | null;
  usdt?: number | null;
  platformRate?: number | null;
  markupPercent?: number | null;
  mode: InputMode;
}): number | null {
  const { amountFiat, usdt, platformRate, markupPercent, mode } = args;
  if ((mode === "AmountUsdtOnly" || mode === "Both") && (usdt ?? 0) > 0 && (amountFiat ?? 0) > 0) {
    return rateFromUsdt(+amountFiat!, +usdt!, markupPercent);
  }
  if ((mode === "RateOnly" || mode === "Both") && (platformRate ?? 0) > 0) {
    return round4(+platformRate!);
  }
  return null;
}

/* ---------- Процент к BB ---------- */
export function percentToBb(effectiveRate: number | null | undefined, bbRate: number): number {
  const er = +effectiveRate! || 0;
  const bb = +bbRate || 0;
  if (er > 0 && bb > 0) return er / bb - 1;
  return 0;
}

/* ---------- Агрегаты ордера ---------- */

export interface OrderLike {
  orderAmountFiat: number;   // A
  exchangeRate: number;      // C (курс BB)
  cardTopUp?: number;        // H
  refund?: number;           // I
}

export interface InvoiceLike {
  amountFiat: number;                 // D_i
  effectiveRate?: number | null;      // R_i (если уже посчитан)
  platformRate?: number | null;       // запасной источник курса
}

export interface Aggregates {
  avgRate: number;       // средний курс по инвойсам (4 знака)
  avgPercent: number;    // J
  sumInvoices: number;   // ΣD
  expected: number;      // A*(1+J)
  profitK: number;       // K = expected - ΣD - H + I
  cashGap: number;       // кассовый разрыв: (A+H) - ΣD (если >0 — недопокрыт)
  display: number;       // что показываем в «Итоге»: cashGap>0 ? -cashGap : profitK
}

/**
 * Главная функция: считает метрики ордера. По умолчанию:
 *  - средний курс по инвойсам — ПРОСТОЙ (как в ваших Google Sheet),
 *  - «Итог» показывается с «засечкой по покрытию»: пока ΣD < A+H → всегда минус на недопокрытие.
 */
export function calcOrderAggregates(
  order: OrderLike,
  invoices: InvoiceLike[] = [],
  opts?: { average?: "simple" | "weighted"; gateByCoverage?: boolean }
): Aggregates {
  const average      = opts?.average ?? "simple";
  const gate         = opts?.gateByCoverage ?? true;

  const A = +(order.orderAmountFiat ?? 0);
const C = +(order.exchangeRate    ?? 0);
const H = +(order.cardTopUp       ?? 0);
const I = +(order.refund          ?? 0);

  // Курсы по инвойсам
  const rows = (invoices || []).map(i => ({
    amount: +i.amountFiat || 0,
    rate:   +(i.effectiveRate ?? i.platformRate ?? 0) || 0
  })).filter(r => r.amount > 0 && r.rate > 0);

  const sumD = round2(rows.reduce((s, r) => s + r.amount, 0)); // ΣD

  let avgRate = 0;
  if (rows.length) {
    if (average === "weighted") {
      const wSum = rows.reduce((s, r) => s + r.rate * r.amount, 0);
      avgRate = wSum / (sumD || 1);
    } else {
      avgRate = rows.reduce((s, r) => s + r.rate, 0) / rows.length;
    }
  }
  avgRate = round4(avgRate);

  const J = (C > 0 && avgRate > 0) ? (avgRate / C - 1) : 0; // avgPercent
  const expected = round2(A * (1 + J));                      // A*(1+J)
  const profitK  = round2(expected - sumD - H + I);          // формульный итог
  const cashGap = round2(A - (sumD + H - I));                  // недопокрытие

  // ПРАВИЛО ПОКАЗА «Итога»: пока не покрыли A+H — показываем отрицательное недопокрытие,
  // после покрытия — показываем формульный итог (может быть и минусом, если реально в минус).
  const display = gate && cashGap > 0 ? -cashGap : profitK;

  return { avgRate, avgPercent: J, sumInvoices: sumD, expected, profitK, cashGap, display };
}
